import React, { useState, useEffect, useCallback } from "react";
import Layout from "./Layout";
import {
  PiTrashBold,
  PiBrainBold,
  PiFileTextBold,
  PiTagBold,
  PiHardDrivesBold,
  PiWarningCircleBold
} from "react-icons/pi";
import { useAuth } from "../contexts/AuthContext";
import "./DataRoom.css";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DataRoom = () => {
  // Layout State
  const [searchQuery, setSearchQuery] = useState("");

  // Data Room State
  const { currentUser, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("memories");
  const [memories, setMemories] = useState([]);
  const [files, setFiles] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ FIX: Robust Token Getter (Checks LocalStorage for 'auth_token' which AuthContext uses)
  const getAuthToken = useCallback(() => {
    return localStorage.getItem('auth_token');
  }, []);

  const fetchData = useCallback(async () => {
    // If auth is still loading, wait.
    if (authLoading) return;

    // If not logged in (no currentUser), don't fetch
    if (!currentUser) return;

    setDataLoading(true);
    setError(null);

    try {
      const endpoint = activeTab === "memories"
        ? "/api/dataroom/memories"
        : "/api/dataroom/files";

      const token = getAuthToken();
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${ token }`;
      }

      const response = await fetch(`${ API_BASE_URL }${ endpoint }`, {
        headers: headers,
        credentials: 'include' // Important for session cookies
      });

      // Handle specific auth error
      if (response.status === 401) {
        throw new Error("Session expired. Please log in again.");
      }

      const result = await response.json();

      if (result.success) {
        if (activeTab === "memories") setMemories(result.data);
        else setFiles(result.data);
      } else {
        // Fallback for success: false but no error code
        if (activeTab === "memories") setMemories([]);
        else setFiles([]);
      }
    } catch (err) {
      console.error("❌ Data Room Error:", err);
      setError(err.message || "Could not load Data Room contents.");
    } finally {
      setDataLoading(false);
    }
  }, [activeTab, getAuthToken, authLoading, currentUser]);

  // ✅ FIX: UseEffect dependency on fetchData and auth state
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item? The AI will forget this context.")) return;

    if (!currentUser) {
      alert("Authentication lost. Please refresh.");
      return;
    }

    try {
      const endpoint = activeTab === "memories"
        ? `/api/dataroom/memories/${ id }`
        : `/api/dataroom/files/${ id }`;

      const token = getAuthToken();
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${ token }`;
      }

      const response = await fetch(`${ API_BASE_URL }{ endpoint }`, {
        method: 'DELETE',
        headers: headers,
        credentials: 'include' // Important for session cookies
      });

      const result = await response.json();

      if (result.success || response.ok) {
        // Optimistic UI update
        if (activeTab === "memories") {
          setMemories(prev => prev.filter(m => m._id !== id));
        } else {
          setFiles(prev => prev.filter(f => f._id !== id));
        }
      } else {
        alert("Failed to delete item.");
      }
    } catch (err) {
      console.error("Delete Error:", err);
      alert("Error connecting to server.");
    }
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // ✅ FIX: Logic for displaying "Please Login" vs "Loading"
  // If loading: show spinner
  // If not loading AND no user: show login error
  // If not loading AND user AND error: show error
  // Else: show content
  const showLoginError = !authLoading && !currentUser;
  const showLoading = authLoading || dataLoading;

  return (
    <Layout searchQuery={searchQuery} setSearchQuery={setSearchQuery}>
      <div className="dataroom-container">
        {/* Header Section */}
        <div className="dataroom-header">
          <div>
            <h1>Data Room</h1>
            <p className="subtitle">Manage the long-term memories and files your AI uses for context.</p>
          </div>

          {/* Toggle Tabs */}
          <div className="dataroom-tabs">
            <button
              className={`tab-btn ${ activeTab === 'memories' ? 'active' : '' }`}
              onClick={() => setActiveTab('memories')}
            >
              <PiBrainBold className="tab-icon" />
              Saved Memories
            </button>
            <button
              className={`tab-btn ${ activeTab === 'files' ? 'active' : '' }`}
              onClick={() => setActiveTab('files')}
            >
              <PiHardDrivesBold className="tab-icon" />
              Files
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="dataroom-content">
          {showLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Syncing with Data Room...</p>
            </div>
          ) : showLoginError ? (
            <div className="error-state">
              <PiWarningCircleBold size={32} />
              <p>Please log in to view your Data Room.</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <PiWarningCircleBold size={32} />
              <p>{error}</p>
              <button className="retry-btn" onClick={() => fetchData()}>Retry</button>
            </div>
          ) : (
            <>
              {/* MEMORIES LIST */}
              {activeTab === 'memories' && (
                <div className="list-grid">
                  {memories.length === 0 ? (
                    <div className="empty-state">
                      <PiBrainBold size={48} opacity={0.3} />
                      <p>No memories saved yet.</p>
                      <small>Chat with the AI and it will automatically save important facts here.</small>
                    </div>
                  ) : (
                    memories.map((mem) => (
                      <div key={mem._id} className="dataroom-card memory-card">
                        <div className="card-icon">
                          <PiBrainBold />
                        </div>
                        <div className="card-body">
                          <div className="card-content">{mem.content}</div>
                          <div className="card-meta">
                            <span className={`tag-badge ${ mem.type }`}>{mem.type}</span>
                            {mem.tags && mem.tags.map((tag, idx) => (
                              <span key={idx} className="tag-pill"><PiTagBold /> {tag}</span>
                            ))}
                          </div>
                        </div>
                        <button className="delete-btn" onClick={() => handleDelete(mem._id)} title="Delete Memory">
                          <PiTrashBold />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* FILES LIST */}
              {activeTab === 'files' && (
                <div className="list-grid">
                  {files.length === 0 ? (
                    <div className="empty-state">
                      <PiFileTextBold size={48} opacity={0.3} />
                      <p>No files in Data Room.</p>
                      <small>Upload documents during chat to see them appear here.</small>
                    </div>
                  ) : (
                    files.map((file) => (
                      <div key={file._id} className="dataroom-card file-card">
                        <div className="card-icon file-icon">
                          <PiFileTextBold />
                        </div>
                        <div className="card-body">
                          <div className="card-title">{file.originalName}</div>
                          <div className="card-meta">
                            <span className="file-info">{file.mimeType}</span>
                            <span className="dot">•</span>
                            <span className="file-info">{formatSize(file.size)}</span>
                          </div>
                          {file.summary && (
                            <div className="file-summary">
                              {file.summary.length > 120 ? file.summary.substring(0, 120) + "..." : file.summary}
                            </div>
                          )}
                        </div>
                        <button className="delete-btn" onClick={() => handleDelete(file._id)} title="Delete File">
                          <PiTrashBold />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DataRoom;